// Archivio locale dell'app dell'auditor (IndexedDB): pacchetto per lavorare senza rete, sopralluoghi, coda e foto.
// Regola: nulla si perde. Una operazione o una foto esce dalla coda SOLO quando il server ha risposto in modo definitivo.

const DB_VERSION = 1;
let dbPromise = null;
let owner = "anon";

// Un archivio per ogni persona: su un tablet condiviso i dati di un auditor non li vede (ne' li perde) un altro.
export function setOwner(id) {
  const next = String(id || "anon");
  if (next !== owner) { owner = next; dbPromise = null; }
}

export function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("IndexedDB non disponibile")); return; }
    const req = indexedDB.open(`dvr-field-${owner}`, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv", { keyPath: "key" });
      if (!db.objectStoreNames.contains("surveys")) db.createObjectStore("surveys", { keyPath: "id" });
      if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "qid", autoIncrement: true });
      if (!db.objectStoreNames.contains("blobs")) db.createObjectStore("blobs", { keyPath: "uuid" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

const reqP = (r) => new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });
function tx(store, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const out = fn(t.objectStore(store));
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

export const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`);

// ---- chiave/valore (pacchetto, ultimo utente)
export async function kvGet(key) { const db = await openDb(); const r = await reqP(db.transaction("kv").objectStore("kv").get(key)); return r ? r.value : null; }
export async function kvPut(key, value) { await tx("kv", "readwrite", (s) => s.put({ key, value })); }

// ---- sopralluoghi
export async function putSurvey(rec) { await tx("surveys", "readwrite", (s) => s.put({ ...rec, local_at: new Date().toISOString() })); }
export async function getSurvey(id) { const db = await openDb(); return reqP(db.transaction("surveys").objectStore("surveys").get(id)); }
export async function listSurveys() { const db = await openDb(); return reqP(db.transaction("surveys").objectStore("surveys").getAll()); }

// ---- coda delle operazioni
export async function queueAll() { const db = await openDb(); const all = await reqP(db.transaction("queue").objectStore("queue").getAll()); return all.sort((a, b) => a.qid - b.qid); }
export async function queueAdd(op) { await tx("queue", "readwrite", (s) => s.add({ ...op, at: new Date().toISOString() })); }
export async function queuePut(op) { await tx("queue", "readwrite", (s) => s.put(op)); }
export async function queueDelete(qid) { await tx("queue", "readwrite", (s) => s.delete(qid)); }

// Un'operazione che sostituisce una precedente non ancora inviata (stessa risposta, stessi dati...): resta una sola.
export async function queueUpsert(op) {
  if (op.ckey) {
    const same = (await queueAll()).find((o) => o.ckey === op.ckey && !o.failed);
    if (same) { await queuePut({ ...same, data: op.data, at: new Date().toISOString() }); return; }
  }
  await queueAdd(op);
}

// ---- foto e documenti in attesa
export async function blobPut(uuid, blob) { await tx("blobs", "readwrite", (s) => s.put({ uuid, blob })); }
export async function blobGet(uuid) { const db = await openDb(); const r = await reqP(db.transaction("blobs").objectStore("blobs").get(uuid)); return r ? r.blob : null; }
export async function blobDelete(uuid) { await tx("blobs", "readwrite", (s) => s.delete(uuid)); }

export async function pendingCount(surveyId) {
  const all = await queueAll();
  return all.filter((o) => !o.failed && (!surveyId || o.survey_id === surveyId)).length;
}

// L'estensione deve corrispondere al contenuto: il server controlla i primi byte.
export function extFor(blob) {
  const t = (blob && blob.type) || "";
  if (t.includes("png")) return "png";
  if (t.includes("webp")) return "webp";
  if (t.includes("heic") || t.includes("heif")) return "heic";
  if (t.includes("pdf")) return "pdf";
  return "jpg";
}

// Foto: si riduce prima di conservarla (meno spazio sul telefono, invio piu' veloce quando torna la rete).
export async function compressImage(file, maxSide = 1600, quality = 0.72) {
  if (!file.type || !file.type.startsWith("image/") || typeof createImageBitmap === "undefined") return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}
